import { useEffect, useMemo, useState } from "react";
import { ComingSoon } from "../../components/ComingSoon.js";
import { PageSEOSection } from "../../components/PageSEOSection.js";
import { ReadingExercise } from "../../components/ReadingExercise.js";
import { MotifBand, motifLabel } from "../../components/Motifs.js";
import { overallSummary } from "../../utils/progress.js";
import { langCodeToName } from "../../utils/appTheme.js";
import { usePageContext } from "vike-react/usePageContext";
const C = {
  bg: 'var(--bg)', surf: 'var(--surf)', surf2: 'var(--surf2)',
  border: 'var(--border)', text: 'var(--text)', muted: 'var(--muted)', dim: 'var(--dim)',
  accentBg: 'var(--accent-bg)', accentBorder: 'var(--accent-border)',
};
const AF = {
  h: 'var(--font-h, "Space Grotesk", system-ui, sans-serif)',
  b: "'DM Sans', system-ui, sans-serif",
  m: "'DM Mono', 'Courier New', monospace",
};

function useIsMobile() {
  const [mobile, setMobile] = useState(false);
  useEffect(() => {
    const check = () => setMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return mobile;
}

type Tab = "reading" | "listening";

const langNameMap: Record<string, string> = {
  es: 'spanish', pt: 'portuguese', fr: 'french',
  it: 'italian', ca: 'catalan', ro: 'romanian', fi: 'finnish',
};

export default function Page() {
  const pageContext = usePageContext();
  const langCode = pageContext.routeParams?.language || 'es';
  const comingSoon = pageContext.routeParams?.comingSoon === 'true';
  const isMobile = useIsMobile();

  const languageName = langCodeToName(langCode);
  const languageSlug = langNameMap[langCode] || 'spanish';
  const hp = isMobile ? '16px' : '48px';

  const [tab, setTab] = useState<Tab>("reading");
  const [attempts, setAttempts] = useState(0);
  const [accuracy, setAccuracy] = useState(0);
  const [selfLevel, setSelfLevel] = useState<"A1"|"A2"|"B1"|"B2"|"C1"|"C2">("A1");

  useEffect(() => {
    const s = overallSummary();
    setAttempts(s.attempts);
    setAccuracy(s.accuracy);
    try {
      const stored = localStorage.getItem("selfComprehensionLevel");
      const valid = ["A1","A2","B1","B2","C1","C2"];
      if (stored && valid.includes(stored)) setSelfLevel(stored as any);
    } catch {}
  }, []);

  const listeningLevel = useMemo(() => {
    if (attempts < 10) return "superbeginner" as const;
    if (attempts < 40) return accuracy >= 60 ? "intermediate" as const : "beginner" as const;
    if (attempts < 120) return accuracy >= 65 ? "intermediate" as const : "beginner" as const;
    return accuracy >= 75 ? "advanced" as const : "intermediate" as const;
  }, [attempts, accuracy]);

  if (comingSoon) return <ComingSoon language={langCode} />;

  const hasReading = ['es','pt','fi'].includes(langCode);

  const LEVEL_DESCS: Record<string, string> = {
    A1: 'Very basic sentences', A2: 'Simple conversations',
    B1: 'Main points of clear speech', B2: 'Complex topics',
    C1: 'Demanding content', C2: 'Everything',
  };

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: C.bg }}>
      {/* MotifBand header */}
      <div style={{ position: 'relative', overflow: 'hidden', borderBottom: `1px solid ${C.border}` }}>
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.55,
          WebkitMaskImage: 'linear-gradient(to left, #000, transparent 60%)',
          maskImage: 'linear-gradient(to left, #000, transparent 60%)',
        }}>
          <MotifBand language={languageName} height={isMobile ? 96 : 112} opacity={0.7}/>
        </div>
        <div style={{ position: 'relative', padding: `22px ${hp} 18px` }}>
          <div style={{ fontFamily: AF.m, fontSize: 10, color: 'var(--accent)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 8 }}>
            {motifLabel(languageName)}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <span style={{ fontSize: 18, lineHeight: 1 }}>📖</span>
            <h1 style={{ fontFamily: AF.h, fontSize: isMobile ? 22 : 26, fontWeight: 700, color: C.text, margin: 0, letterSpacing: '-0.7px' }}>
              Reading
            </h1>
          </div>
          <p style={{ fontFamily: AF.b, fontSize: 13, color: C.muted, margin: 0 }}>
            Build reading and listening skills at your own pace.
          </p>
        </div>
      </div>

      {/* Tab switcher */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}`, padding: `0 ${hp}`, background: C.surf }}>
        {(['reading','listening'] as Tab[]).map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            fontFamily: AF.b, fontSize: 14, fontWeight: tab === t ? 600 : 400,
            padding: '12px 0', marginRight: 28, border: 'none',
            borderBottom: `2px solid ${tab === t ? 'var(--accent)' : 'transparent'}`,
            background: 'transparent',
            color: tab === t ? 'var(--accent)' : C.muted,
            cursor: 'pointer', transition: 'color 0.15s, border-color 0.15s',
          }}>
            {t === 'reading' ? '📖 Reading' : '🎧 Listening'}
          </button>
        ))}
      </div>

      <div style={{ padding: `28px ${hp}` }}>
        {/* Reading tab */}
        {tab === 'reading' && (
          hasReading ? (
            <ReadingExercise languageCode={langCode} />
          ) : (
            <div style={{ textAlign: 'center', padding: '64px 0' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📖</div>
              <p style={{ fontFamily: AF.b, fontSize: 15, color: C.muted }}>
                Reading exercises coming soon for {languageName}.
              </p>
            </div>
          )
        )}

        {/* Listening tab */}
        {tab === 'listening' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 640 }}>
            {/* Self-assessed level */}
            <div style={{ padding: '18px 20px', borderRadius: 10, border: `1px solid ${C.border}`, background: C.surf }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <div>
                  <div style={{ fontFamily: AF.b, fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 2 }}>Your listening level</div>
                  <div style={{ fontFamily: AF.b, fontSize: 12, color: C.muted }}>Updates the dashboard comprehension track</div>
                </div>
                <select
                  value={selfLevel}
                  onChange={e => {
                    const v = e.target.value as typeof selfLevel;
                    setSelfLevel(v);
                    try { localStorage.setItem("selfComprehensionLevel", v); } catch {}
                  }}
                  style={{
                    fontFamily: AF.m, fontSize: 12, padding: '6px 10px', borderRadius: 7,
                    border: `1px solid ${C.border}`, background: C.surf2, color: C.text, cursor: 'pointer',
                  }}
                >
                  {(["A1","A2","B1","B2","C1","C2"] as const).map(l => (
                    <option key={l} value={l}>{l}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                {(["A1","A2","B1","B2","C1","C2"] as const).map(l => (
                  <div key={l} style={{
                    padding: '8px 10px', borderRadius: 7, fontSize: 12,
                    border: `1px solid ${l === selfLevel ? 'var(--accent)' : C.border}`,
                    background: l === selfLevel ? C.accentBg : 'transparent',
                  }}>
                    <span style={{ fontFamily: AF.m, fontWeight: 700, color: l === selfLevel ? 'var(--accent)' : C.text }}>{l}</span>
                    <span style={{ fontFamily: AF.b, color: C.muted }}> — {LEVEL_DESCS[l]}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Dreaming Spanish */}
            {langCode === 'es' && (
              <div style={{ padding: '18px 20px', borderRadius: 10, border: `1px solid ${C.border}`, background: C.surf }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div>
                    <div style={{ fontFamily: AF.h, fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 3 }}>Dreaming Spanish</div>
                    <div style={{ fontFamily: AF.b, fontSize: 12, color: C.muted }}>Comprehensible input videos matched to your level</div>
                  </div>
                  <span style={{ fontSize: 24 }}>🇪🇸</span>
                </div>
                <p style={{ fontFamily: AF.b, fontSize: 13, color: C.muted, marginBottom: 14 }}>
                  Based on your conjugation practice ({accuracy}% accuracy over {attempts} attempts), we suggest the <strong style={{ color: C.text }}>{listeningLevel}</strong> level.
                </p>
                <a
                  href={`https://www.dreamingspanish.com/browse?level=${listeningLevel}`}
                  target="_blank" rel="noreferrer noopener follow index"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '9px 18px', borderRadius: 8,
                    background: 'var(--accent)', color: C.bg,
                    fontFamily: AF.b, fontSize: 13, fontWeight: 700, textDecoration: 'none',
                  }}
                >
                  Open Dreaming Spanish
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M2 11L11 2M11 2H5M11 2V8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </a>
              </div>
            )}

            {/* Português do Zero */}
            {langCode === 'pt' && (
              <div style={{ padding: '18px 20px', borderRadius: 10, border: `1px solid ${C.border}`, background: C.surf }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div>
                    <div style={{ fontFamily: AF.h, fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 3 }}>Português do Zero</div>
                    <div style={{ fontFamily: AF.b, fontSize: 12, color: C.muted }}>Brazilian Portuguese comprehensible input for beginners</div>
                  </div>
                  <span style={{ fontSize: 24 }}>🇧🇷</span>
                </div>
                <a
                  href="https://www.youtube.com/@portuguesdomundo"
                  target="_blank" rel="noreferrer noopener follow index"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '9px 18px', borderRadius: 8,
                    background: 'var(--accent)', color: C.bg,
                    fontFamily: AF.b, fontSize: 13, fontWeight: 700, textDecoration: 'none',
                  }}
                >
                  Open on YouTube
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M2 11L11 2M11 2H5M11 2V8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </a>
              </div>
            )}

            {/* Tutor CTA */}
            <div style={{ marginTop: 8, paddingTop: 20, borderTop: `1px solid ${C.border}` }}>
              <div style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 14 }}>
                Ready for real conversations?
              </div>
              <a
                href="https://www.tusclasesparticulares.cl/profesores/cynthia-valenzuela-osorio.htm"
                target="_blank" rel="noopener noreferrer follow index"
                style={{ textDecoration: 'none', display: 'inline-flex' }}
              >
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  padding: '14px 18px', borderRadius: 10,
                  background: C.surf, border: `1px solid ${C.border}`,
                }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                    background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontFamily: AF.b, fontSize: 12, fontWeight: 700, color: C.bg,
                  }}>CV</div>
                  <div>
                    <div style={{ fontFamily: AF.b, fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 2 }}>
                      Book a lesson with Cynthia
                    </div>
                    <div style={{ fontFamily: AF.b, fontSize: 12, color: C.muted }}>
                      Native teacher · First class free
                    </div>
                  </div>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginLeft: 8, flexShrink: 0 }}>
                    <path d="M3 8h10M9 4l4 4-4 4" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </a>
            </div>
          </div>
        )}
      </div>

      <PageSEOSection pageType="comprehension" languageName={languageSlug} />
    </div>
  );
}
